import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { userProfileService } from "../../api/services/userProfileService";
import { getQueryParamRaw } from "../../utils/queryStringUtils";
import Loader from "../Loader/Loader";
import "./ResetPassword.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const email = getQueryParamRaw(search, "email");
    const token = getQueryParamRaw(search, "token");

    if (email) {
      localStorage.setItem("reset_password_email", email);
    }
    if (token) {
      localStorage.setItem("reset_password_token", token);
    }
  }, [search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMessage("Passwords do not match. Please try again.");
      return;
    }

    // Validate password strength (minimum 8 characters)
    if (formData.newPassword.length < 8) {
      setErrorMessage("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);

    try {
      const savedEmail =
        getQueryParamRaw(search, "email") ||
        localStorage.getItem("reset_password_email");
      const savedToken =
        getQueryParamRaw(search, "token") ||
        localStorage.getItem("reset_password_token");

      if (!savedEmail || !savedToken) {
        setErrorMessage("Reset link is invalid or expired. Please try again.");
        return;
      }

      const payload = {
        UserID: 0,
        UserName: savedEmail,
        Token: savedToken,
        NewPassword: formData.newPassword,
        ConfirmNewPassword: formData.confirmPassword,
      };

      const res = await userProfileService.resetPassword(payload, savedEmail);
      const ok =
        Number(res?.success) === 1 ||
        res?.success === true ||
        res?.success === "1";
      if (!ok) {
        setErrorMessage(
          res?.msg ?? res?.message ?? "Failed to reset password. Please try again.",
        );
        return;
      }

      // Success - show success message and redirect
      setSuccessMessage(
        res?.msg ?? res?.message ?? "Password reset successfully! Redirecting to login...",
      );
      localStorage.removeItem("reset_password_email");
      localStorage.removeItem("reset_password_token");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Reset password error:", error);
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="reset-password-page">
      {/* Loader Overlay */}
      {loading && <Loader />}

      <div className="reset-password-container">
        <div className="reset-password-content">
          <h1 className="reset-password-title">SET PASSWORD</h1>

          <div className="reset-password-instructions">
            <p>Choose a strong password to secure your account.</p>
            <p>
              Make sure it's easy for you to remember but hard for others to
              guess.
            </p>
          </div>

          {errorMessage && (
            <div className="reset-password-error">{errorMessage}</div>
          )}

          {successMessage && (
            <div className="reset-password-success">{successMessage}</div>
          )}

          <form onSubmit={handleSubmit} className="reset-password-form">
            <div className="reset-password-field">
              <label htmlFor="newPassword" className="reset-password-label">
                New Password
              </label>
              <div className="reset-password-input-wrapper">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  className="reset-password-input"
                  placeholder="Enter Password"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  required
                />
                <button
                  type="button"
                  className="reset-password-toggle"
                  onClick={toggleNewPasswordVisibility}
                  aria-label="Toggle password visibility"
                >
                  {showNewPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="reset-password-field">
              <label
                htmlFor="confirmPassword"
                className="reset-password-label"
              >
                Confirm Password
              </label>
              <div className="reset-password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  className="reset-password-input"
                  placeholder="Enter Password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
                <button
                  type="button"
                  className="reset-password-toggle"
                  onClick={toggleConfirmPasswordVisibility}
                  aria-label="Toggle password visibility"
                >
                  {showConfirmPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="reset-password-submit-btn"
              disabled={loading}
            >
              {loading ? "Processing..." : "CREATE NEW PASSWORD"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
